import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GameOptions from "./GameOptions"; // Assumes same directory
import { useGameBot } from "./useGameBot"; // Assumes same directory
import PlayerNameInput from "./PlayerNameInput";

// Coin Flip Game Component
export const CoinFlipGame = ({ onBack }) => {
  const navigate = useNavigate();
  const [numCoins, setNumCoins] = useState(100);
  const [coins, setCoins] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  // New State for Bot
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  
  // Player Names
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  const startGame = () => {
    const initialCoins = Array(numCoins).fill("gold");
    setCoins(initialCoins);
    setGameStarted(true);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
  };

  const isValidMove = useCallback(
    (index, currentCoins = coins) => {
      if (currentCoins[index] !== "gold") return false;
      return currentCoins.length - index >= 50;
    },
    [coins]
  );

  const getValidMoves = useCallback(
    (currentCoins = coins) => {
      const validMoves = [];
      for (let i = 0; i < currentCoins.length; i++) {
        if (isValidMove(i, currentCoins)) {
          validMoves.push(i);
        }
      }
      return validMoves;
    },
    [coins, isValidMove]
  );

  useEffect(() => {
    if (gameStarted && coins.length > 0) {
      const validMoves = getValidMoves(coins);
      if (validMoves.length === 0) {
        setGameOver(true);
        setWinner(currentPlayer === 1 ? 2 : 1);
      }
    }
  }, [coins, currentPlayer, gameStarted, getValidMoves]);

  // Core move logic
  const makeMove = useCallback(
    (index) => {
      if (!gameStarted || gameOver || !isValidMove(index)) {
        return;
      }

      const newCoins = [...coins];
      for (let i = index; i < index + 50 && i < newCoins.length; i++) {
        newCoins[i] = newCoins[i] === "gold" ? "black" : "gold";
      }

      setCoins(newCoins);
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    },
    [coins, gameStarted, gameOver, isValidMove, currentPlayer]
  );

  // Bot Strategy
  const getBotMove = useCallback(
    (diff, currentGameState) => {
      const { coins } = currentGameState;
      const validMoves = [];
      // Calculate valid moves locally to ensure freshness
      for (let i = 0; i < coins.length; i++) {
        if (coins[i] === "gold" && coins.length - i >= 50) {
          validMoves.push(i);
        }
      }

      if (validMoves.length === 0) return null;

      // Easy: Random
      if (diff === "Easy") {
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }

      // Medium: Random
      if (diff === "Medium") {
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }

      // Hard: Smart Lookahead (Greedy for win)
      if (diff === "Hard") {
        // Check if any move leads immediately to no moves for opponent (Win)
        for (let moveIndex of validMoves) {
          // Simulate
          const newCoins = [...coins];
          for (let i = moveIndex; i < moveIndex + 50 && i < newCoins.length; i++) {
            newCoins[i] = newCoins[i] === "gold" ? "black" : "gold";
          }
          
          // Check if opponent has moves
          let opponentHasMoves = false;
          for (let i = 0; i < newCoins.length; i++) {
             if (newCoins[i] === "gold" && newCoins.length - i >= 50) {
                opponentHasMoves = true;
                break;
             }
          }

          if (!opponentHasMoves) {
            return moveIndex; // Winning move!
          }
        }

        // If no winning move, pick random
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }
      return validMoves[0];
    },
    []
  );

  // Hook for Bot
  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove,
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { coins },
    difficulty,
  });

  const handleCoinClick = (index) => {
    // Prevent clicking during bot turn
    if (gameMode === "PvC" && currentPlayer === 2) return;
    if (isBotThinking) return;
    makeMove(index);
  };

  const resetGame = () => {
    setGameStarted(false);
    setCoins([]);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
  };

  const validMoves = gameStarted ? getValidMoves() : [];
  
  const getPlayerName = (id) => {
      if (gameMode === "PvC" && id === 2) return "Computer";
      return id === 1 ? player1Name : player2Name;
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
          Coin Flip Challenge
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Strategic two-player game of coin flipping mastery
        </p>

        {!gameStarted ? (
          <div className="text-center">
             <GameOptions
              gameMode={gameMode}
              setGameMode={setGameMode}
              difficulty={difficulty}
              setDifficulty={setDifficulty}
              onStart={startGame}
            />
            
            <PlayerNameInput
                player1Name={player1Name}
                setPlayer1Name={setPlayer1Name}
                player2Name={player2Name}
                setPlayer2Name={setPlayer2Name}
                gameMode={gameMode}
            />

            <div className="max-w-lg mx-auto border-t pt-6 mb-6">
              <label className="block text-lg font-medium text-gray-700 mb-2">
                Number of Coins (minimum 50):
              </label>
              <input
                type="number"
                min="50"
                value={numCoins}
                onChange={(e) => setNumCoins(parseInt(e.target.value))}
                className="px-4 py-2 text-xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
              <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                <p>• Players take turns clicking on gold coins</p>
                <p>
                  • You can only click a gold coin if there are at least 49
                  coins after it
                </p>
                <p>
                  • Clicking flips the selected coin and the next 49 coins (50
                  total)
                </p>
                <p>• Gold coins turn black, black coins turn gold</p>
                <p>• Game ends when no valid moves remain</p>
                <p>• The player who cannot make a move loses</p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="text-xl font-semibold">
                {gameOver ? (
                  <span className="text-green-600">
                    🎉 {getPlayerName(winner)} Wins!
                  </span>
                ) : (
                  <span>
                    Current Player:
                    <span
                      className={
                        currentPlayer === 1
                          ? "text-blue-600 ml-2"
                          : "text-red-600 ml-2"
                      }
                    >
                      {getPlayerName(currentPlayer)}
                    </span>
                    {isBotThinking && <span className="ml-2 text-sm text-gray-500">(Thinking...)</span>}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                Valid moves: {validMoves.length}
              </div>
              <button
                onClick={resetGame}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                New Game
              </button>
            </div>

            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 mb-4 justify-items-center">
              {coins.map((color, index) => (
                <div key={index} className="text-center">
                  <div className="text-[10px] md:text-xs text-gray-400 mb-1">{index + 1}</div>
                  <button
                    onClick={() => handleCoinClick(index)}
                    disabled={gameOver || !isValidMove(index) || isBotThinking}
                    className={`
                      w-10 h-10 md:w-12 md:h-12 rounded-full border-2 transition-all duration-200 font-bold text-xs md:text-sm flex items-center justify-center
                      ${
                        color === "gold"
                          ? "bg-yellow-400 border-yellow-600 text-yellow-800"
                          : "bg-gray-800 border-gray-600 text-gray-300"
                      }
                      ${
                        isValidMove(index) && !gameOver
                          ? "hover:scale-110 hover:shadow-lg cursor-pointer ring-2 ring-blue-300"
                          : "cursor-not-allowed opacity-75"
                      }
                      ${validMoves.includes(index) ? "animate-pulse" : ""}
                    `}
                    title={`Coin ${index + 1} - ${color} ${
                      isValidMove(index) ? "(Valid move)" : ""
                    }`}
                  >
                    {color === "gold" ? "🟡" : "⚫"}
                  </button>
                </div>
              ))}
            </div>

            <div className="text-sm text-gray-600 text-center">
              Click on a highlighted gold coin to make your move.
              {!gameOver && validMoves.length > 0 && (
                <span> Valid coins are pulsing and have blue rings.</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
